import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtINR } from "@/lib/format";
import { PURPOSE_LABEL } from "@/lib/activityPurposes";
import {
  Activity, Calendar as CalendarIcon, ClipboardCheck, MoveRight, FileText, User as UserIcon, ChevronDown, ChevronRight as ChevronRightIcon,
} from "lucide-react";

const RANGE_OPTIONS = [
  { d: 1,  l: "Today" },
  { d: 7,  l: "7 days" },
  { d: 14, l: "14 days" },
  { d: 30, l: "30 days" },
];

const KIND_META = {
  activity:             { icon: Activity,        tone: "text-sky-700 bg-sky-50 border-sky-200",         label: "Activity"    },
  followup_completed:   { icon: ClipboardCheck,  tone: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Follow-up" },
  meeting:              { icon: CalendarIcon,    tone: "text-amber-800 bg-amber-50 border-amber-200",   label: "Meeting"     },
  deal_stage:           { icon: MoveRight,       tone: "text-indigo-700 bg-indigo-50 border-indigo-200", label: "Stage move" },
  quotation:            { icon: FileText,        tone: "text-violet-700 bg-violet-50 border-violet-200", label: "Quotation"  },
};

function UserRow({ row, expanded, onToggle }) {
  const totals = row.totals || {};
  const sum = Object.values(totals).reduce((a, b) => a + (b || 0), 0);
  return (
    <Card className="rounded-3xl border-border/60 shadow-soft" data-testid={`admin-act-user-${row.user_id}`}>
      <CardContent className="p-4">
        <button
          type="button"
          onClick={onToggle}
          className="w-full flex items-center justify-between gap-3 text-left tap"
          data-testid={`admin-act-user-toggle-${row.user_id}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-2xl bg-primary-50 grid place-items-center shrink-0">
              <UserIcon className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate">{row.user_name}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{row.user_role || "member"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="text-xs">{sum} events</Badge>
            {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />}
          </div>
        </button>
        <div className="grid grid-cols-5 gap-1.5 mt-3">
          {[
            { k: "activities",          n: totals.activities,          l: "Activities",     icon: Activity },
            { k: "followups_completed", n: totals.followups_completed, l: "Follow-ups",     icon: ClipboardCheck },
            { k: "meetings",            n: totals.meetings,            l: "Meetings",       icon: CalendarIcon },
            { k: "deal_stage_moves",    n: totals.deal_stage_moves,    l: "Deal moves",     icon: MoveRight },
            { k: "quotations",          n: totals.quotations,          l: "Quotes",         icon: FileText },
          ].map((t) => (
            <div key={t.k} className="text-center p-2 rounded-2xl bg-muted/30">
              <t.icon className="h-3.5 w-3.5 mx-auto text-muted-foreground" />
              <p className="text-base font-bold tabular-nums mt-0.5">{t.n || 0}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{t.l}</p>
            </div>
          ))}
        </div>
        {expanded && (
          <div className="mt-4 space-y-3" data-testid={`admin-act-user-days-${row.user_id}`}>
            {(row.days || []).map((day) => (
              <div key={day.day}>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">{day.day} — {day.events.length} event{day.events.length === 1 ? "" : "s"}</p>
                <div className="space-y-1.5">
                  {day.events.map((e, i) => {
                    const meta = KIND_META[e.kind] || { icon: Activity, tone: "text-muted-foreground bg-muted/40 border-border", label: e.kind };
                    const Icon = meta.icon;
                    const purposeLbl = e.purpose ? PURPOSE_LABEL[e.purpose] : null;
                    return (
                      <div key={`${e.kind}-${e.ref_id || i}`} className={`px-3 py-2 rounded-2xl border ${meta.tone} flex items-start gap-2`}>
                        <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold truncate">{e.label || meta.label}</p>
                          <p className="text-[10px] opacity-80">
                            {meta.label}
                            {e.sub ? ` · ${String(e.sub).replaceAll("_", " ")}` : ""}
                            {purposeLbl ? ` · ${purposeLbl}` : ""}
                            {e.amount ? ` · ${fmtINR(e.amount)}` : ""}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminActivityFeed() {
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/activity-feed/daily?days=${days}`)
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-5" data-testid="admin-activity-feed-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Admin Activity</h1>
          <p className="text-sm text-muted-foreground mt-1">Per-user daily roll-up of every action across the team — activities, follow-ups, meetings, deal moves, quotations.</p>
        </div>
        <div className="flex items-center gap-1.5" data-testid="admin-act-range">
          {RANGE_OPTIONS.map((o) => (
            <Button
              key={o.d}
              size="sm"
              variant={days === o.d ? "default" : "outline"}
              onClick={() => setDays(o.d)}
              className="rounded-2xl h-9"
              data-testid={`admin-act-range-${o.d}`}
            >{o.l}</Button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 w-full rounded-3xl" />)}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-10 text-center">
            <Activity className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-semibold">No activity in this window</p>
            <p className="text-xs text-muted-foreground mt-1">Try widening the range.</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <UserRow
            key={r.user_id}
            row={r}
            expanded={!!expanded[r.user_id]}
            onToggle={() => setExpanded((s) => ({ ...s, [r.user_id]: !s[r.user_id] }))}
          />
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground text-center pt-2">
        <Link to="/team" className="underline">Team Pulse</Link> shows the full leaderboard. This feed is the admin-only daily activity drill-down.
      </p>
    </div>
  );
}
